import graphql__d10e939ef26a422cd0ff from "./__generated__/SimpleMenuNavigationItem_navigationItem.graphql.js";
import graphql__e7c8175330866e32286c from "./__generated__/SimpleMenuNavigationItem_navigationItemDetails.graphql.js";import { useCallback, useEffect, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { CompositeItem } from "reakit/Composite";
import classNames from "classnames";

import SidePanel from "../../Utils/SidePanel";
import { Desktop, NonDesktop, useResponsiveFlag } from "../../Utils/ResponsiveQueries";
import { colorIsAdaptedForForeground } from "../../Utils/colorUtils";
import { useSimpleMenuMainNavigation } from "./SimpleMenuMainNavigation";
import Icon from "../../Utils/Icon";
import { useNonModalLocation } from "../../Utils/routing";
import SimpleMenuNavigationItemDropdownMenu from "./SimpleMenuNavigationItemDropdownMenu";
import { useButtonProps } from "../../Buttons/useButtonProps";
import KEY_CODES from "../../Utils/keyCodes";

const SimpleMenuNavigationItem = props => {
  const navigationItem = useFragment(
    SimpleMenuNavigationItem_navigationItem,
    props.navigationItem
  );
  const { name, url, isSubMenu } = navigationItem;
  const { isTopLevel, primaryColor, onClick, childrenItems, compositeState, onKeyDown } =
    props;

  const iconClassName =
    navigationItem.iconClass &&
    classNames([
      navigationItem.iconClass,
      {
        "text-primary": !isTopLevel && colorIsAdaptedForForeground(primaryColor)
      }
    ]);

  const [showChildren, setShowChildren] = useState(false);
  const { showMenuBackdrop, setShowMenuBackdrop } = useSimpleMenuMainNavigation();
  const [refocusAfterNextRender, setRefocusAfterNextRender] = useState(false);

  const currentPath = useNonModalLocation();
  const isActive = !isSubMenu && currentPath === url;
  const hasChildren = !!childrenItems?.length;

  const { isDesktop } = useResponsiveFlag();

  const className = classNames({
    "nav-item": true,
    dropdown: hasChildren,
    active: isActive,
    "padding-bottom-sm": !isTopLevel
  });

  const showSubMenu = useCallback(() => {
    setShowChildren(true);
  }, []);

  const closeSubMenu = useCallback(() => {
    setShowChildren(false);
    setShowMenuBackdrop(false);
  }, [setShowMenuBackdrop]);

  useEffect(() => {
    if (!showMenuBackdrop && showChildren) closeSubMenu();
  }, [closeSubMenu, showMenuBackdrop, showChildren]);

  useEffect(() => {
    if (refocusAfterNextRender) {
      setRefocusAfterNextRender(false);
    }
  }, [refocusAfterNextRender]);

  const handleClick = useCallback(
    e => {
      if (hasChildren) {
        if (showChildren) closeSubMenu();
        else showSubMenu();
      }

      onClick && onClick(e);
    },
    [closeSubMenu, hasChildren, onClick, showChildren, showSubMenu]
  );

  const handleSubMenuKeyDown = useCallback(
    e => {
      if (e.keyCode === KEY_CODES.ESCAPE) {
        closeSubMenu();
        setRefocusAfterNextRender(true);
      }
    },
    [closeSubMenu]
  );

  const buttonBaseClassName = classNames([
    "nav-link",
    "text-btn-lg",
    "btn-md",
    "h-100",
    {
      "btn-unstyled": isTopLevel || isDesktop,
      "d-flex": isTopLevel,
      "w-100": isTopLevel,
      "justify-content-center": isTopLevel,
      "btn-primary": isTopLevel,
      "text-primary-inverse": isTopLevel,
      "btn-centered": isTopLevel,
      "nav-link-top-level": isTopLevel,
      "nav-link-sub-level": !isTopLevel,
      "bg-white-80": !isTopLevel
    }
  ]);

  const innerClassName = classNames("button-inner", {
    "text-btn-lg": isTopLevel,
    "d-flex": !isTopLevel,
    "align-items-center": !isTopLevel,
    "text-default": !isTopLevel
  });

  const {
    passthroughProps,
    className: buttonClassName,
    content: buttonContent
  } = useButtonProps({
    href: url,
    className: buttonBaseClassName,
    onClick: handleClick,
    iconClassName: iconClassName,
    label: name,
    innerClassName: innerClassName,
    isActive: isActive
  });

  return (
    <li className={className}>
      {isSubMenu && (
        <CompositeItem
          {...compositeState}
          {...passthroughProps}
          as="button"
          type="button"
          className={buttonClassName}
          aria-expanded={showMenuBackdrop}
          onKeyDown={onKeyDown || handleSubMenuKeyDown}
        >
          {buttonContent}
        </CompositeItem>
      )}

      {!isSubMenu && (
        <CompositeItem
          {...compositeState}
          {...passthroughProps}
          as="a"
          className={buttonClassName}
          onKeyDown={onKeyDown}
        >
          {buttonContent}
        </CompositeItem>
      )}

      {isSubMenu && hasChildren && (
        <>
          <Desktop>
            <SimpleMenuNavigationItemDropdownMenu
              primaryColor={primaryColor}
              childrenItems={childrenItems}
              onClick={onClick}
              onKeyDown={handleSubMenuKeyDown}
              shouldFocusSubMenu={showChildren}
            />
          </Desktop>

          <NonDesktop>
            <SidePanel isOpen={showChildren} onRequestClose={handleClick}>
              <div className="side-panel-title text-btn-lg with-icon">
                {iconClassName && <Icon className={iconClassName} />}

                <span className="with-icon-text">{name}</span>
              </div>

              <ul className="side-panel-menu">
                {childrenItems.map(navigationItem => (
                  <SimpleMenuNavigationItem
                    key={navigationItem.id}
                    navigationItem={navigationItem}
                    onClick={handleClick}
                  />
                ))}
              </ul>
            </SidePanel>
          </NonDesktop>
        </>
      )}
    </li>
  );
};

const SimpleMenuNavigationItem_navigationItem = graphql__d10e939ef26a422cd0ff;

graphql__e7c8175330866e32286c;

export default SimpleMenuNavigationItem;
